import type { JSX } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "../../api/queryKeys";
import { useMyTenants } from "../../app/TenantGate";
import { NotificationSettings } from "../../pwa/NotificationSettings";
import { useAuth } from "../../shared/auth/AuthContext";
import { useI18n } from "../../shared/i18n/I18nContext";
import type { MessageKey } from "../../shared/i18n/messages.de";
import { isLocale, supportedLocales } from "../../shared/i18n/translate";
import { LegalLinkList } from "../../shared/legal/LegalLinks";
import { isThemePreference, usePreferences } from "../../shared/preferences/Preferences";
import { useMe } from "../../shared/session/useMe";
import { accountSectionId } from "../../shared/session/signOutRequest";
import type { GuardedSignOut } from "../../shared/session/signOutRequest";
import { useTenantSelectionStore } from "../../shared/session/tenantSelection";
import { cx } from "../../shared/ui/classNames";
import { Icon } from "../../shared/ui/Icon";
import { SignOutControls } from "../../shared/ui/SignOutControls";
import styles from "../../shared/ui/ui.module.css";
import { useFocusHashTarget } from "../../shared/ui/useFocusHashTarget";
import { GeofenceReminderSettings } from "../geofence/GeofenceReminderSettings";
import moreStyles from "./MorePage.module.css";

/** Properties of {@link SettingsSections}. */
export interface SettingsSectionsProps {
  /** Heading of the card with language and appearance. */
  readonly preferencesTitleKey: MessageKey;
  /**
   * The sign-out of the page (`useGuardedSignOut`), held by the page so „Mehr“ can hand an open
   * confirmation on to „Einstellungen“ when the window becomes wide (F-84).
   */
  readonly signOut: GuardedSignOut;
}

/**
 * Content of the settings pages „Mehr“ (phones) and „Einstellungen“ (account menu, F-74): language
 * and appearance, the notifications of this device (UX-024, fragment `#notifications`), the
 * reminders at the workplace, the account with the change of employer and the sign-out, and the
 * legal links. A fragment of the address (`#notifications`, `#account`) focuses its section.
 *
 * @param props - Component properties.
 * @returns The sections.
 */
export function SettingsSections(props: SettingsSectionsProps): JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  const { locale, theme, setLocale, setTheme } = usePreferences();
  const me = useMe();
  const displayName = me.data?.displayName ?? user?.displayName ?? null;
  const tenantStore = useTenantSelectionStore();
  const queryClient = useQueryClient();
  const tenants = useMyTenants();
  useFocusHashTarget(true);

  const sections = (
    <>
      <section className={styles["card"]} aria-labelledby="more-settings">
        <h2 id="more-settings">{t(props.preferencesTitleKey)}</h2>
        <div className={styles["formGrid"]}>
          <label className={styles["field"]}>
            <span>{t("app.language")}</span>
            <select
              value={locale}
              onChange={(event) => {
                const value = event.target.value;

                if (isLocale(value)) {
                  setLocale(value);
                }
              }}
            >
              {supportedLocales.map((option) => (
                <option key={option} value={option} lang={option}>
                  {t(`more.language.${option}`)}
                </option>
              ))}
            </select>
          </label>
          <label className={styles["field"]}>
            <span>{t("app.theme")}</span>
            <select
              value={theme}
              onChange={(event) => {
                const value = event.target.value;

                if (isThemePreference(value)) {
                  setTheme(value);
                }
              }}
            >
              <option value="system">{t("app.theme.system")}</option>
              <option value="light">{t("app.theme.light")}</option>
              <option value="dark">{t("app.theme.dark")}</option>
            </select>
          </label>
        </div>
      </section>

      <div id="notifications" className={moreStyles["target"]} tabIndex={-1}>
        <NotificationSettings />
      </div>

      <GeofenceReminderSettings />

      <section
        id={accountSectionId}
        className={cx(styles["card"], moreStyles["target"])}
        aria-labelledby="more-account"
        tabIndex={-1}
      >
        <h2 id="more-account">{t("more.account")}</h2>
        {displayName === null ? null : (
          <p className={moreStyles["account"]}>
            <Icon name="account" className={moreStyles["entryIcon"]} />
            <span>{t("auth.signedInAs", { name: displayName })}</span>
          </p>
        )}
        {(tenants.data?.length ?? 0) > 1 && user !== null ? (
          <button
            type="button"
            className={styles["button"]}
            onClick={() => {
              tenantStore.clear(user.subject);
              queryClient.removeQueries({
                predicate: (query) => query.queryKey[0] !== queryKeys.myTenants[0],
              });
            }}
          >
            {t("tenantSelection.switch")}
          </button>
        ) : null}
        <SignOutControls signOut={props.signOut} />
      </section>

      <LegalLinkList />
    </>
  );

  return sections;
}
